<template>
  <div class="new-booking-page">
    <front-navigation></front-navigation>

    <div class="app-container">
      <div class="header-row">
        <div class="header-text">
          <h2 class="earthwerk-font">Plan Your Visit</h2>
          <p class="subtitle">When, who, and how</p>
        </div>
        <div class="cart-btn" @click="openCartSidebar">
          <v-icon color="white" size="24">mdi-cart</v-icon>
          <span class="cart-badge" v-if="cartCount > 0">{{ cartCount }}</span>
        </div>
      </div>

      <!-- COMBINED NOTICE -->
      <div class="id-notice">
        <v-icon small color="#e67e22">mdi-information-outline</v-icon>
        <div class="id-notice-text">
          <p>
            Please carry your original identification document (passport, national ID, or driver's licence). It will be verified at the Ol Pejeta gate before entry is granted.
          </p>
          <p class="infant-note">
            <v-icon x-small color="#74a533">mdi-baby-face-outline</v-icon>
            Infants (below 2 years) enter free of charge.
          </p>
        </div>
      </div>

      <!-- 1. DATES -->
      <div class="section-card">
        <h3 class="section-title"><v-icon small color="#74a533">mdi-calendar-range</v-icon> Dates</h3>

        <v-layout row wrap>
          <v-flex xs12 sm6 class="pa-1">
            <v-dialog ref="dialog" persistent v-model="modal1" lazy full-width width="290px" :return-value.sync="booking.check_in">
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-on="on"
                  label="CHECK-IN"
                  v-model="booking.check_in"
                  readonly
                  solo
                  flat
                  class="earthwerk-font input-field"
                  background-color="#f9fbf6"
                ></v-text-field>
              </template>
              <v-date-picker v-model="booking.check_in" :min="start_date" @input="onCheckInSelected" scrollable color="#74a533">
                <v-spacer></v-spacer>
                <v-btn flat color="#74a533" @click="modal1 = false" class="earthwerk-font">CANCEL</v-btn>
                <v-btn flat color="#74a533" @click="saveCheckIn" class="earthwerk-font">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-flex>

          <v-flex xs12 sm6 class="pa-1">
            <v-dialog ref="dialog1" persistent v-model="modal2" lazy full-width width="290px" :return-value.sync="booking.check_out">
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-on="on"
                  label="CHECK-OUT"
                  v-model="booking.check_out"
                  readonly
                  solo
                  flat
                  class="earthwerk-font input-field"
                  background-color="#f9fbf6"
                ></v-text-field>
              </template>
              <v-date-picker v-model="booking.check_out" :min="minCheckOutDate" @input="onCheckOutSelected" scrollable color="#74a533">
                <v-spacer></v-spacer>
                <v-btn flat color="#74a533" @click="modal2 = false" class="earthwerk-font">CANCEL</v-btn>
                <v-btn flat color="#74a533" @click="saveCheckOut" class="earthwerk-font">OK</v-btn>
              </v-date-picker>
            </v-dialog>
          </v-flex>
        </v-layout>

        <div v-if="booking.check_in && booking.check_out" class="badge-success mt-2">
          <v-icon small color="#74a533">mdi-weather-night</v-icon>
          {{ nights > 0 ? (nights + ' night' + (nights > 1 ? 's' : '')) : 'Day visit' }}
        </div>
      </div>

      <!-- 2. GUESTS -->
      <div class="section-card">
        <h3 class="section-title"><v-icon small color="#74a533">mdi-account-group</v-icon> Guests</h3>

        <v-menu offset-y v-model="guestsMenu" :close-on-content-click="false" :full-width="true">
          <template v-slot:activator="{ on }">
            <v-btn :depressed="true" block class="earthwerk-font guest-trigger" v-on="on">
              <span v-if="totalGuests === 0">Click here to add number of Guests</span>
              <span v-else>{{ totalGuests }} Guest{{ totalGuests > 1 ? 's' : '' }}</span>
              <v-icon small color="#74a533" class="ml-2">mdi-chevron-down</v-icon>
            </v-btn>
          </template>

          <v-list class="guest-list">
            <div v-if="loadingGuests" class="popover-loading">
              <v-progress-circular indeterminate color="#74a533" size="20" />
              <span class="ml-2">Loading guests…</span>
            </div>

            <template v-else>
              <v-list-tile v-for="(guest, key) in guestFees" :key="key" class="guest-tile">
                <v-layout row align-center style="width: 100%; padding: 8px 0;">
                  <v-flex xs7>
                    <p class="body-2 guest-name" :title="guest.payment_person_description">
                      {{ guest.payment_person }} ({{ guest.payment_person_currency }} {{ guest.payment_person_amount }})
                    </p>
                  </v-flex>
                  <v-flex xs2 class="text-center">
                    <v-btn
                      :depressed="true"
                      @click="updateGuest(key, -1)"
                      :disabled="guest.payment_guests === 0"
                      dark round small color="error"
                    >
                      <v-icon small>mdi-minus</v-icon>
                    </v-btn>
                  </v-flex>
                  <v-flex xs1 class="text-center">
                    <h4>{{ guest.payment_guests || 0 }}</h4>
                  </v-flex>
                  <v-flex xs2 class="text-center">
                    <v-btn :depressed="true" @click="updateGuest(key, 1)" dark round small color="primary">
                      <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-list-tile>

              <v-list-tile>
                <v-layout row wrap>
                  <v-flex xs12 class="text-right">
                    <v-btn :depressed="true" dark small color="primary" @click="guestsMenu = false" class="earthwerk-font">
                      Close
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-list-tile>
            </template>
          </v-list>
        </v-menu>
      </div>

      <!-- 3. VEHICLES -->
      <div class="section-card">
        <h3 class="section-title"><v-icon small color="#74a533">mdi-car</v-icon> Vehicles</h3>

        <v-menu offset-y v-model="vehiclesMenu" :close-on-content-click="false" :full-width="true">
          <template v-slot:activator="{ on }">
            <v-btn :depressed="true" block class="earthwerk-font vehicle-trigger" v-on="on">
              <span v-if="totalVehicles === 0 && !vehicleSeparate">Click here to add number of Vehicles</span>
              <span v-else-if="vehicleSeparate">Vehicle Fee Booked Separately ✓</span>
              <span v-else>{{ totalVehicles }} Vehicle{{ totalVehicles > 1 ? 's' : '' }}</span>
              <v-icon small color="#74a533" class="ml-2">mdi-chevron-down</v-icon>
            </v-btn>
          </template>

          <v-list class="vehicle-list">
            <div v-if="loadingVehicles" class="popover-loading">
              <v-progress-circular indeterminate color="#74a533" size="20" />
              <span class="ml-2">Loading vehicles…</span>
            </div>

            <template v-else>
              <v-list-tile
                v-for="(vehicle, key) in vehicleFees"
                :key="key"
                class="vehicle-tile"
                :disabled="vehicleSeparate"
              >
                <v-layout row align-center style="width: 100%; padding: 8px 0;">
                  <v-flex xs7>
                    <p class="body-2 vehicle-name">
                      {{ vehicle.vehicle_charge_category }}
                      ({{ vehicle.vehicle_charge_cost_currency }} {{ vehicle.vehicle_charge_category_cost }})
                    </p>
                  </v-flex>
                  <v-flex xs2 class="text-center">
                    <v-btn
                      :depressed="true"
                      @click="updateVehicle(key, -1)"
                      :disabled="vehicleSeparate || vehicle.vehicles === 0"
                      round small color="error" dark
                    >
                      <v-icon small>mdi-minus</v-icon>
                    </v-btn>
                  </v-flex>
                  <v-flex xs1 class="text-center">
                    <h4>{{ vehicle.vehicles || 0 }}</h4>
                  </v-flex>
                  <v-flex xs2 class="text-center">
                    <v-btn
                      :depressed="true"
                      @click="updateVehicle(key, 1)"
                      :disabled="vehicleSeparate"
                      small color="primary" dark
                    >
                      <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-list-tile>

              <v-list-tile>
                <v-layout row wrap>
                  <v-flex xs12>
                    <v-checkbox
                      v-model="vehicleSeparate"
                      @change="onVehicleSeparateChange"
                      label="Vehicle fee already booked separately."
                      color="primary"
                      hide-details
                      class="vehicle-separate-check"
                    ></v-checkbox>
                  </v-flex>
                </v-layout>
              </v-list-tile>

              <v-list-tile>
                <v-layout row wrap>
                  <v-flex xs12 class="text-right">
                    <v-btn :depressed="true" dark small color="primary" @click="vehiclesMenu = false" class="earthwerk-font">
                      Close
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-list-tile>
            </template>
          </v-list>
        </v-menu>
      </div>

      <!-- 4. ACTION (Total Info only) -->
      <div class="action-section" v-if="isValid">
        <div class="total-row">
          <div class="total-info">
            <span class="total-label">Running Total</span>
            <span class="total-value">KES {{ grandTotal.toLocaleString() }}</span>
          </div>
          <span class="total-meta">{{ nights > 0 ? nights + ' night' + (nights > 1 ? 's' : '') : 'Day visit' }}</span>
        </div>
      </div>
    </div>

    <!-- FLOATING CTA -->
    <button class="floating-cta" :class="{ 'disabled': !isValid }" :disabled="!isValid" @click="continueToActivities">
      <v-icon small color="#ffffff">mdi-arrow-right</v-icon>
      <span>Continue</span>
    </button>

    <!-- CART SIDEBAR -->
    <transition name="fade">
      <div class="sidebar-overlay" v-if="sidebarOpen" @click="closeSidebar"></div>
    </transition>
    <transition name="slide">
      <div class="sidebar-wrapper" v-show="sidebarOpen">
        <div class="sidebar-header">
          <h3 class="sidebar-title">
            <v-icon small class="mr-1">mdi-cart</v-icon>
            Your Booking Cart
            <span v-if="cartCount > 0" class="sidebar-badge">{{ cartCount }}</span>
          </h3>
          <v-btn icon small @click="closeSidebar" class="close-sidebar-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <side-bar ref="sidebar" @close="closeSidebar"></side-bar>
      </div>
    </transition>
  </div>
</template>

<script>
import FrontNavigation from '../Repeated/FrontNavigation'
import SideBar from '../Repeated/SideBar.vue'

export default {
  name: 'NewBooking',
  components: { FrontNavigation, SideBar },
  data() {
    return {
      modal1: false,
      modal2: false,
      sidebarOpen: false,
      guestsMenu: false,
      vehiclesMenu: false,
      loadingGuests: false,
      loadingVehicles: false,
      start_date: this.$moment().format('YYYY-MM-DD'),
      minCheckOutDate: this.$moment().format('YYYY-MM-DD'),

      booking: {
        days: 0,
        check_in: '',
        check_out: '',
        currency: localStorage.getItem('currency')
      },

      guestFees: [],
      vehicleFees: [],
      vehicleSeparate: false
    }
  },
  computed: {
    nights() {
      if (!this.booking.check_in || !this.booking.check_out) return 0
      const diff = Math.ceil(
        (new Date(this.booking.check_out) - new Date(this.booking.check_in)) / 86400000
      )
      return Math.max(0, diff)
    },
    totalGuests() {
      return this.guestFees.reduce((sum, g) => sum + (Number(g.payment_guests) || 0), 0)
    },
    totalVehicles() {
      return this.vehicleFees.reduce((sum, v) => sum + (Number(v.vehicles) || 0), 0)
    },
    cartCount() {
      let count = 0
      if (this.totalGuests > 0) count++
      if (this.totalVehicles > 0 || this.vehicleSeparate) count++
      return count
    },
    isValid() {
      return (
        this.booking.check_in &&
        this.booking.check_out &&
        this.totalGuests > 0 &&
        (this.totalVehicles > 0 || this.vehicleSeparate)
      )
    },
    guestTotal() {
      const nights = this.nights || 1
      return this.guestFees.reduce((sum, g) => {
        const count = g.payment_guests || 0
        const price = g.payment_person_amount || 0
        const discount = g.payment_person_discount || 0
        const perGuest = price * (1 - discount / 100)
        return sum + perGuest * count * nights
      }, 0)
    },
    vehicleTotal() {
      if (this.vehicleSeparate) return 0
      const nights = this.nights || 1
      return this.vehicleFees.reduce((sum, v) => {
        const count = v.vehicles || 0
        const price = v.vehicle_charge_category_cost || 0
        const discount = v.discount || 0
        const perVehicle = price * (1 - discount / 100)
        return sum + perVehicle * count * nights
      }, 0)
    },
    grandTotal() {
      return this.guestTotal + this.vehicleTotal
    }
  },
  created() {
    this.loadState()
    this.initializeGuests()
    this.initializeVehicles()
  },
  methods: {
    loadState() {
      const booking = JSON.parse(localStorage.getItem('booking') || '{}')
      if (booking.check_in) this.booking.check_in = booking.check_in
      if (booking.check_out) this.booking.check_out = booking.check_out
      if (booking.days) this.booking.days = booking.days

      const vSep = localStorage.getItem('vehicle_separate')
      if (vSep !== null) this.vehicleSeparate = JSON.parse(vSep)
    },

    // ==================== GUESTS ====================
    initializeGuests() {
      const conservationData = localStorage.getItem('conservation_data')
      if (conservationData && JSON.parse(conservationData).length !== 0) {
        this.guestFees = JSON.parse(conservationData)
        return
      }
      const storeFees =
        (this.$store.state.ConservationFeeStore &&
         this.$store.state.ConservationFeeStore.all_conservation_fees) || []
      if (storeFees.length > 0) {
        this.guestFees = JSON.parse(JSON.stringify(storeFees)).map(f => ({
          ...f,
          payment_guests: f.payment_guests || 0
        }))
        return
      }
      this.fetchGuests()
    },
    async fetchGuests() {
      this.loadingGuests = true
      try {
        const gRes = await this.$store.dispatch('get_all_conservation_fees').catch(() => null)
        let guests = []
        if (gRes && gRes.data) guests = gRes.data
        else if (Array.isArray(gRes)) guests = gRes
        else if (this.$store.state.ConservationFeeStore &&
                 this.$store.state.ConservationFeeStore.all_conservation_fees) {
          guests = this.$store.state.ConservationFeeStore.all_conservation_fees
        }
        if (guests.length > 0) {
          const savedGuests = JSON.parse(localStorage.getItem('conservation_data') || '[]')
          this.guestFees = JSON.parse(JSON.stringify(guests)).map(g => {
            const saved = savedGuests.find(sg => sg.payment_public_id === g.payment_public_id)
            return { ...g, payment_guests: saved ? (saved.payment_guests || 0) : 0 }
          })
        }
      } catch (e) {
        console.error('Error fetching guests', e)
      } finally {
        this.loadingGuests = false
      }
    },
    updateGuest(index, delta) {
      const guest = this.guestFees[index]
      const newCount = Math.max(0, (Number(guest.payment_guests) || 0) + delta)
      this.$set(this.guestFees, index, { ...guest, payment_guests: newCount })
      this.saveGuests()
    },
    saveGuests() {
      localStorage.setItem('conservation_data', JSON.stringify(this.guestFees))
      const adults = this.guestFees
        .filter(g => g.payment_person.toLowerCase().includes('adult'))
        .reduce((s, g) => s + (Number(g.payment_guests) || 0), 0)
      const children = this.guestFees
        .filter(g => g.payment_person.toLowerCase().includes('child'))
        .reduce((s, g) => s + (Number(g.payment_guests) || 0), 0)
      localStorage.setItem('guests', JSON.stringify({ adults, children, total: adults + children }))
      this.$root.$emit('cart-updated')
    },

    // ==================== VEHICLES ====================
    initializeVehicles() {
      const vehicleData = localStorage.getItem('vehicle_data')
      if (vehicleData && JSON.parse(vehicleData).length !== 0) {
        this.vehicleFees = JSON.parse(vehicleData)
        return
      }
      const storeFees =
        (this.$store.state.VehicleFeeStore &&
         this.$store.state.VehicleFeeStore.all_vehicle_fees) || []
      if (storeFees.length > 0) {
        this.vehicleFees = JSON.parse(JSON.stringify(storeFees)).map(v => ({
          ...v,
          vehicles: v.vehicles || 0
        }))
        return
      }
      this.fetchVehicles()
    },
    async fetchVehicles() {
      this.loadingVehicles = true
      try {
        const vRes = await this.$store.dispatch('get_all_vehicle_fees').catch(() => null)
        let vehicles = []
        if (vRes && vRes.data) vehicles = vRes.data
        else if (Array.isArray(vRes)) vehicles = vRes
        else if (this.$store.state.VehicleFeeStore &&
                 this.$store.state.VehicleFeeStore.all_vehicle_fees) {
          vehicles = this.$store.state.VehicleFeeStore.all_vehicle_fees
        }
        if (vehicles.length > 0) {
          const savedVehicles = JSON.parse(localStorage.getItem('vehicle_data') || '[]')
          this.vehicleFees = JSON.parse(JSON.stringify(vehicles)).map(v => {
            const saved = savedVehicles.find(sv => sv.vehicle_charge_public_id === v.vehicle_charge_public_id)
            return { ...v, vehicles: saved ? (saved.vehicles || 0) : 0 }
          })
        }
      } catch (e) {
        console.error('Error fetching vehicles', e)
      } finally {
        this.loadingVehicles = false
      }
    },
    updateVehicle(index, delta) {
      if (this.vehicleSeparate && delta > 0) {
        this.vehicleSeparate = false
        this.saveVehicleSeparateState()
      }
      const vehicle = this.vehicleFees[index]
      const newCount = Math.max(0, (Number(vehicle.vehicles) || 0) + delta)
      this.$set(this.vehicleFees, index, { ...vehicle, vehicles: newCount })
      this.saveVehicles()
    },
    onVehicleSeparateChange() {
      if (this.vehicleSeparate) {
        this.vehicleFees = this.vehicleFees.map(v => ({ ...v, vehicles: 0 }))
        this.saveVehicles()
      }
      this.saveVehicleSeparateState()
    },
    saveVehicleSeparateState() {
      localStorage.setItem('vehicle_separate', JSON.stringify(this.vehicleSeparate))
      this.$root.$emit('cart-updated')
    },
    saveVehicles() {
      localStorage.setItem('vehicle_data', JSON.stringify(this.vehicleFees))
      this.$root.$emit('cart-updated')
    },

    // ==================== DATES ====================
    onCheckInSelected() {
      this.updateCheckOutMin()
      this.saveDates()
      setTimeout(() => {
        this.$refs.dialog.save(this.booking.check_in)
        this.modal1 = false
      }, 300)
    },
    onCheckOutSelected() {
      if (this.booking.check_in) {
        const a = this.$moment(this.booking.check_in)
        const b = this.$moment(this.booking.check_out)
        this.booking.days = Math.max(0, b.diff(a, 'days'))
      }
      this.saveDates()
      setTimeout(() => {
        this.$refs.dialog1.save(this.booking.check_out)
        this.modal2 = false
      }, 300)
    },
    saveCheckIn() {
      this.$refs.dialog.save(this.booking.check_in)
      this.updateCheckOutMin()
      this.saveDates()
    },
    saveCheckOut() {
      this.$refs.dialog1.save(this.booking.check_out)
      if (this.booking.check_in) {
        const a = this.$moment(this.booking.check_in)
        const b = this.$moment(this.booking.check_out)
        this.booking.days = Math.max(0, b.diff(a, 'days'))
      }
      this.saveDates()
    },
    updateCheckOutMin() {
      if (this.booking.check_in) {
        this.minCheckOutDate = this.booking.check_in
        if (this.booking.check_out && this.$moment(this.booking.check_out).isBefore(this.minCheckOutDate)) {
          this.booking.check_out = this.minCheckOutDate
        }
      } else {
        this.minCheckOutDate = this.start_date
      }
    },
    saveDates() {
      if (this.booking.check_in && this.booking.check_out) {
        const a = this.$moment(this.booking.check_in)
        const b = this.$moment(this.booking.check_out)
        this.booking.days = Math.max(0, b.diff(a, 'days'))
      }
      localStorage.setItem('booking', JSON.stringify(this.booking))
      
      this.$root.$emit('cart-updated')
    },

    // ==================== CART ====================
    openCartSidebar() {
      this.sidebarOpen = true
      document.body.style.overflow = 'hidden'
    },
    closeSidebar() {
      this.sidebarOpen = false
      document.body.style.overflow = ''
    },
    continueToActivities() {
      if (this.isValid) {
        window.scrollTo(0, 0)
        this.$router.push('/activities')
      }
    }
  },
  watch: {
    '$store.state.ConservationFeeStore.all_conservation_fees': {
      handler(newFees) {
        if (newFees && newFees.length > 0 && this.guestFees.length === 0) {
          const savedGuests = JSON.parse(localStorage.getItem('conservation_data') || '[]')
          this.guestFees = JSON.parse(JSON.stringify(newFees)).map(g => {
            const saved = savedGuests.find(sg => sg.payment_public_id === g.payment_public_id)
            return { ...g, payment_guests: saved ? (saved.payment_guests || 0) : 0 }
          })
        }
      },
      deep: true
    },
    '$store.state.VehicleFeeStore.all_vehicle_fees': {
      handler(newFees) {
        if (newFees && newFees.length > 0 && this.vehicleFees.length === 0) {
          const savedVehicles = JSON.parse(localStorage.getItem('vehicle_data') || '[]')
          this.vehicleFees = JSON.parse(JSON.stringify(newFees)).map(v => {
            const saved = savedVehicles.find(sv => sv.vehicle_charge_public_id === v.vehicle_charge_public_id)
            return { ...v, vehicles: saved ? (saved.vehicles || 0) : 0 }
          })
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
@font-face {
  font-family: 'Earthwerk';
  src: url('PizzaDude.dk-Earthwerk.otf') format('opentype');
  font-weight: normal;
  font-style: normal;
}

.new-booking-page {
  background: transparent;
  min-height: 100vh;
  padding: 24px 16px 120px; /* space for floating button */
  position: relative;
}
.app-container { max-width: 600px; margin: 0 auto; }

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  margin-top: 10px;
}
.header-text h2 {
  color: #2d4a1e;
  font-size: 28px;
  margin: 0 0 4px 0;
  text-shadow: 0 1px 3px rgba(255,255,255,0.5);
}
.subtitle { color: #5e6e4e; font-size: 14px; margin: 0; }

.cart-btn {
  position: relative;
  width: 50px; height: 50px;
  background: #74a533;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(116,165,51,0.3);
  cursor: pointer;
  transition: transform 0.2s;
  flex-shrink: 0;
}
.cart-btn:hover { transform: scale(1.05); }
.cart-badge {
  position: absolute;
  top: -4px; right: -4px;
  background: #ff5252; color: #fff;
  font-size: 12px; font-weight: bold;
  width: 22px; height: 22px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #fff;
}

.earthwerk-font {
  font-family: 'Earthwerk', sans-serif;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* INFO NOTICE */
.id-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fff8e1;
  #border-left: 4px solid #e67e22;
  padding: 14px 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  font-size: 13px;
  color: #8a5300;
  line-height: 1.55;
}
.id-notice .v-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
.id-notice-text { flex: 1; }
.id-notice-text p { margin: 0; }
.id-notice-text p + p { margin-top: 8px; }
.infant-note {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #4a6b22;
  font-weight: 600;
}

/* CARDS */
.section-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}
.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Earthwerk', sans-serif;
  font-size: 15px;
  color: #2d4a1e;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 16px 0;
}
.input-field >>> .v-label {
  font-family: 'Earthwerk', sans-serif !important;
  font-size: 0.9rem !important;
  opacity: 0.9 !important;
}
.badge-success {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #eef3e8;
  color: #74a533;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

/* TRIGGER */
.guest-trigger,
.vehicle-trigger {
  width: 100% !important;
  min-height: 52px !important;
  background: #f9fbf6 !important;
  color: #2d4a1e !important;
  border: 1px solid #eef1ea !important;
  border-radius: 12px !important;
  font-size: 13.5px !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  padding: 0 16px !important;
  text-transform: none !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}
.guest-trigger >>> .v-btn__content,
.vehicle-trigger >>> .v-btn__content {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  color: #2d4a1e !important;
}

/* MENU LIST */
.guest-list,
.vehicle-list {
  background: #fff;
  border-radius: 12px;
  padding: 8px !important;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
}
.guest-tile,
.vehicle-tile {
  border-bottom: 1px solid #f2f4ee !important;
  min-height: 56px !important;
}
.guest-tile:last-of-type,
.vehicle-tile:last-of-type {
  border-bottom: none !important;
}
.guest-name,
.vehicle-name {
  margin: 0;
  font-size: 13.5px !important;
  font-weight: 600;
  color: #2d4a1e !important;
  line-height: 1.4;
}
.vehicle-separate-check >>> .v-label {
  font-size: 12.5px !important;
  color: #2d4a1e !important;
}

/* ACTION */
.action-section {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  margin-top: 10px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}
.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.total-info { display: flex; flex-direction: column; }
.total-label {
  font-size: 11px;
  color: #7b8f6b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.total-value {
  font-size: 22px;
  font-weight: bold;
  color: #2d4a1e;
}
.total-meta {
  font-size: 13px;
  color: #7b8f6b;
  font-weight: 600;
}

/* FLOATING CTA (Copied from Index) */
.floating-cta {
  position: fixed;
  right: 18px;
  bottom: calc(18px + env(safe-area-inset-bottom));
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  background: #74a533;
  color: #ffffff;
  font-weight: 700;
  font-size: 14px;
  padding: 14px 22px;
  border-radius: 40px;
  box-shadow: 0 10px 26px -6px rgba(116, 165, 51, 0.55),
              0 4px 12px -4px rgba(0, 0, 0, 0.2);
  z-index: 55;
  transition: transform 0.12s, box-shadow 0.12s, opacity 0.2s;
}
.floating-cta:hover {
  box-shadow: 0 14px 30px -6px rgba(116, 165, 51, 0.7),
              0 6px 16px -4px rgba(0, 0, 0, 0.25);
}
.floating-cta:active {
  transform: scale(0.96);
}
.floating-cta.disabled {
  background: #dce5d4;
  box-shadow: none;
  cursor: not-allowed;
  opacity: 0.7;
}

/* SIDEBAR */
.sidebar-overlay {
  position: fixed;
  top: 0; right: 0; bottom: 0; left: 0;
  background: rgba(15,23,33,0.55);
  z-index: 999;
  backdrop-filter: blur(1px);
}
.fade-enter-active, .fade-leave-active { transition: opacity 0.25s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

.sidebar-wrapper {
  position: fixed;
  top: 0; right: 0;
  width: 380px;
  max-width: 92vw;
  height: 100vh;
  background: #fff;
  box-shadow: -6px 0 28px rgba(0,0,0,0.14);
  z-index: 1000;
  display: flex; flex-direction: column; overflow-y: auto;
}
.slide-enter-active, .slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
}
.slide-enter, .slide-leave-to { transform: translateX(100%); }
.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #74a533;
  color: #fff;
  position: sticky;
  top: 0;
  z-index: 10;
}
.sidebar-title {
  font-size: 1.05rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.sidebar-badge {
  background: #ff5252;
  color: #fff;
  border-radius: 999px;
  min-width: 22px; height: 22px;
  font-size: 12px; font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  margin-left: 8px;
}
.close-sidebar-btn {
  color: #fff !important;
  background: rgba(255,255,255,0.18) !important;
  border-radius: 50% !important;
}

@media (max-width: 600px) {
  .new-booking-page { padding: 16px 12px 100px; }
  .header-text h2 { font-size: 22px; }
  .section-card { padding: 16px; border-radius: 12px; }
  .section-title { font-size: 13px; }
  .guest-trigger, .vehicle-trigger { font-size: 12.5px !important; }
  
  .floating-cta {
    right: 14px;
    bottom: calc(14px + env(safe-area-inset-bottom));
    padding: 12px 18px;
    font-size: 13px;
  }

  .sidebar-wrapper {
    width: 100%; max-width: 100%;
    height: 85vh;
    top: auto; bottom: 0;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
  }
  .slide-enter, .slide-leave-to { transform: translateY(100%); }
}
</style>